import axios from "axios";
import { baseUrl } from "@/consts";
import { Cart, CartProduct, ProcessPrecommitmentResponse, Product } from "@/interfaces";
import {
  combinePaths,
  isNonEmptyArray,
  isNonEmptyNumber,
  isNonEmptyString,
  roundCurrency,
} from "@/utils";
import { boolean } from "yup";

export const getItemInStoreQuantity = (item?: CartProduct): number =>
  item ? item.quantity || 0 : 0;

export const getItemShippedQuantity = (item?: CartProduct): number =>
  item ? item.shippingQuantity || 0 : 0;

export const getItemSubtotal = (item?: CartProduct): number => {
  if (!item) return 0;

  const price = item.price || 0;
  const inStoreQty = getItemInStoreQuantity(item);
  const shippedQty = getItemShippedQuantity(item);

  return roundCurrency(price * (inStoreQty + shippedQty));
};

export const getItemInStoreSubtotal = (item?: CartProduct): number => {
  return item
    ? roundCurrency((item.price || 0) * getItemInStoreQuantity(item))
    : 0;
};

export const getItemShippedSubtotal = (item?: CartProduct): number => {
  return item
    ? roundCurrency((item.price || 0) * getItemShippedQuantity(item))
    : 0;
};

export const getItemBalance = (item?: CartProduct, cart?: Cart): number => {
  return item
    ? roundCurrency(getItemSubtotal(item) - (item.coopApplied || 0))
    : 0;
};

export const getItemInStoreBalance = (item?: CartProduct): number => {
  return item
    ? roundCurrency(
        getItemInStoreSubtotal(item) -
          roundCurrency(getItemCoopApplied(item) * getItemInStoreQuantity(item))
      )
    : 0;
};

export const getItemShippedBalance = (item?: CartProduct): number => {
  return item
    ? roundCurrency(
        getItemShippedSubtotal(item) -
          roundCurrency(getItemCoopApplied(item) * getItemShippedQuantity(item))
      )
    : 0;
};

export const getItemCoopApplied = (item?: CartProduct): number => {
  return (
    (item &&
      (isNonEmptyNumber(item.coopApplied) && isNonEmptyNumber(item.quantity)
        ? roundCurrency((item.coopApplied || 0) / (item.quantity || 1))
        : isNonEmptyNumber(item?.shippingQuantity)
        ? roundCurrency((item.coopApplied || 0) / (item?.shippingQuantity || 1))
        : 0)) ||
    0
  );
};

export const getSubtotalCoopTotal = (cart?: Cart): number => {
  return cart && Array.isArray(cart.products)
    ? cart.products.reduce(
        (acc, item) => roundCurrency(acc + (item?.coopApplied || 0)),
        0
      )
    : 0;
};

export const getSubtotalBalance = (cart?: Cart): number => {
  return cart && Array.isArray(cart.products)
    ? cart.products.reduce(
        (acc, item) => roundCurrency(acc + getItemBalance(item, cart)),
        0
      )
    : 0;
};

export const getItemDiscount = (item?: CartProduct): number => {
  return item && isNonEmptyNumber(item.itemDiscount as number)
    ? roundCurrency(item.itemDiscount || 0)
    : 0;
};

export const getCartItemDiscountTotal = (cart?: Cart): number => {
  return cart && Array.isArray(cart.products)
    ? cart.products.reduce(
        (acc, item) => roundCurrency(acc + getItemDiscount(item)),
        0
      )
    : 0;
};

export const hasItemDiscount = (cart?: Cart): boolean => {
  return cart && Array.isArray(cart.products)
    ? cart.products.some((item) => getItemDiscount(item) > 0)
    : false;
};

// Resolves an item discount config against the current line subtotal — used by both
// the dialog (preview) and the controller (when stamping the value).
export const resolveItemDiscountAmount = (
  item?: CartProduct,
  config?: CartProduct["itemDiscountConfig"]
): number => {
  if (!item || !config) return 0;
  const subtotal = getItemSubtotal(item);
  if (config.type === "percentage") {
    return roundCurrency((subtotal * (config.value || 0)) / 100);
  }
  return roundCurrency(config.value || 0);
};

export const getCouponTotal = (cart?: Cart): number => {
  return roundCurrency((cart?.couponQuantity || 0) * (cart?.couponAmount || 0));
};

export const hasInStoreItems = (cart?: Cart): boolean => {
  return isNonEmptyArray(cart?.products)
    ? cart!.products.some((item) => item.quantity || 0)
    : false;
};

export const getInStoreItems = (cart?: Cart) => {
  if (!cart || !Array.isArray(cart.products)) {
    return [];
  }

  return cart.products.filter((item) => item.quantity || 0);
};

export const getInStoreItemsQuantity = (cart?: Cart | CartProduct[]) => {
  return (!Array.isArray(cart) ? getInStoreItems(cart) : cart).reduce(
    (acc, item) => acc + (item.quantity || 0),
    0
  );
};

export const hasShippedItems = (cart?: Cart): boolean => {
  return isNonEmptyArray(cart?.products)
    ? cart!.products.some((item) =>
        isNonEmptyNumber(item.shippingQuantity as number)
      )
    : false;
};

export const getShippedItems = (cart?: Cart) => {
  return isNonEmptyArray(cart?.products)
    ? cart!.products.filter((item) =>
        isNonEmptyNumber(item.shippingQuantity as number)
      )
    : [];
};

export const getShippedItemsQuantity = (cart?: Cart | CartProduct[]) => {
  return (!Array.isArray(cart) ? getShippedItems(cart) : cart).reduce(
    (acc, item) => acc + (item.shippingQuantity || 0),
    0
  );
};

export const getShippingFee = (cart?: Cart): number => {
  return (!hasOffsiteProducts(cart) && hasShippedItems(cart)) ||
    hasOffsiteProducts(cart)
    ? cart?.shippingFee || 0
    : 0;
};

export const getTotalAmount = (cart?: Cart, isOffSite?: boolean): number => {
  const result = roundCurrency(
    getSubtotalBalance(cart) -
      (isOffSite ? cart?.offSiteDiscount || 0 : cart?.discount || 0) -
      getCartItemDiscountTotal(cart) -
      getCouponTotal(cart) +
      getShippingFee(cart)
  );

  return result >= 0 ? result : 0;
};


// export const isProductAvaiable = (product:CartProduct,increment:number)=>{
//   if(product.inventoryCount > )
// }


export const getTotalQuantity = (cart?: Cart): number => {
  return cart && Array.isArray(cart.products)
    ? cart.products.reduce(
        (acc: number, item: CartProduct) =>
          acc + (item.shippingQuantity || 0) + (item.quantity || 0),
        0
      )
    : 0;
};

export const getCartSubtotal = (cart?: Cart): number => {
  return isNonEmptyArray(cart?.products)
    ? cart!.products.reduce((acc: number, item: CartProduct) => {
        // For items that have shippingQuantity > 0, use shippingQuantity
        if ((item.shippingQuantity || item.quantity || 0) > 0) {
          return (
            acc +
            roundCurrency((item.price || 0) * (item.shippingQuantity || 0)) +
            roundCurrency((item.price || 0) * (item.quantity || 0))
          );
        }
        // For in-store items, use quantity minus shippingQuantity
        return (
          acc +
          roundCurrency(
            (item.price || 0) *
              ((item.quantity || 0) - (item.shippingQuantity || 0))
          )
        );
      }, 0)
    : 0;
};

/**
 * Recalculates per-line co-op for the cart using PER-MEDIAID rates fetched from ACB
 * via the backend's /Orders/processprecommitment endpoint. Each cart product can have
 * a different rate based on its mediaId. Falls back to cart.coopEligible (the legacy
 * flat rate) for products that have no mediaId or whose mediaId rate fetch failed.
 *
 * This is now async because rate lookup is async. The 4 callers in CartContext have
 * been updated to await it.
 *
 * Backend is the source of truth — it re-fetches and overrides these rates at order time.
 * The values stamped here are an honest preview for the dealer and are recorded in
 * ApiAuditLogs (via the request body) for any later audit.
 */
export const recalculateCoop = async (
  cart?: Cart,
  coopAvailableBalance?: number,
  acbAllowanceEnabled?:boolean,
  masterDealerId?: string
): Promise<Cart | undefined> => {
  console.log("➡️ Recalculating Co-Op (split: in-store vs shipping)...");

  if (
    !Array.isArray(cart?.products) ||
    typeof coopAvailableBalance !== "number"
  ) {
    console.warn("❌ Invalid cart or coopAvailableBalance", {
      cart,
      coopAvailableBalance,
    });
    return cart;
  }

  // Fail-closed default: if a product has no mediaId, or if ACB couldn't be reached,
  // or no masterDealerId is set, we show $0 co-op for that product. We deliberately
  // do NOT fall back to the legacy cart.coopEligible (90%) — pretending a product
  // qualifies for co-op when ACB hasn't confirmed it leads to orders that promise
  // co-op the dealer never actually receives. Better to show $0 in the cart and let
  // the dealer notice and fix the product's mediaId than to silently lose money on
  // checkout. This matches the backend's fail-closed behavior in OrdersService.Pay().
  const fallbackRate = 0;

  // Fetch per-mediaId rates if we know the dealer. Without a masterDealerId we can't
  // hit the rate endpoint; in that case all products use fallbackRate.
  let mediaIdRates: Record<string, number> = {};
  if (masterDealerId) {
    const productMediaIds = cart.products
      .map((p) => p.mediaId)
      .filter((m): m is string => typeof m === "string" && m.length > 0);

    if (productMediaIds.length > 0) {
      try {
        mediaIdRates = await fetchMediaIdRates(masterDealerId, productMediaIds);
      } catch (error) {
        // Catastrophic fetch failure — proceed with fallbackRate for everything.
        // Don't block cart updates on rate-fetch errors.
        console.warn("⚠️ fetchMediaIdRates failed wholesale, using fallback rate:", error);
      }
    }
  }

  const rateForProduct = (item: CartProduct): number => {
    if (item.mediaId && typeof mediaIdRates[item.mediaId] === "number") {
      return mediaIdRates[item.mediaId];
    }
    return fallbackRate;
  };

  // First pass: total eligible co-op across the cart, per product's own rate.
  // Needed for the global scaling factor that caps total to available balance.
  let totalEligibleCoop = 0;
  cart.products.forEach((item) => {
    const rate = rateForProduct(item);
    const inStoreSubtotal = (item.price || 0) * (item.quantity || 0);
    const shippingSubtotal = (item.price || 0) * (item.shippingQuantity || 0);
    totalEligibleCoop += roundCurrency(
      (inStoreSubtotal + shippingSubtotal) * rate
    );
  });

  // Clamp to [0, 1]: a non-positive balance forces scalingFactor to 0 (so all
  // applied coop becomes 0), and a balance above the total eligible caps at 1
  // (no scaling). This is the hard cap that prevents the cart from applying
  // more co-op than the dealer actually has available.
  const effectiveBalance = Math.max(0, coopAvailableBalance);
  const scalingFactor =
    totalEligibleCoop > 0
      ? Math.min(1, effectiveBalance / totalEligibleCoop)
      : 0;

  console.log("🧾 Total Eligible Coop:", totalEligibleCoop);
  console.log("💡 Scaling Factor:", scalingFactor);
  console.log("📊 Per-mediaId rates:", mediaIdRates);


  // Second pass: apply co-op per line with the line's own rate × scaling factor.
  // Stamp coopRate on the item so it serializes into the /orders/pay request body
  // (and therefore into ApiAuditLogs).
  cart.products.forEach((item, index) => {
    const rate = rateForProduct(item);
    const price = item.price || 0;
    const qtyInStore = item.quantity || 0;
    const qtyShipping = item.shippingQuantity || 0;

    const inStoreSubtotal = price * qtyInStore;
    const shippingSubtotal = price * qtyShipping;


      const appliedCoopInStore = roundCurrency(
        inStoreSubtotal * rate * scalingFactor
      );
      const appliedCoopShipping = roundCurrency(
        shippingSubtotal * rate * scalingFactor
      );

          item.coopInStore = appliedCoopInStore;
          item.coopShipping = appliedCoopShipping;
          item.coop = item.coopApplied = appliedCoopInStore + appliedCoopShipping;
          item.coopRate = rate;


          console.log(
            `🔹 [${index}] ${item.productDescription} (ID: ${item.productId}, mediaId: ${item.mediaId ?? "<none>"}, rate: ${rate})`
          );
          console.log(
            "   ↳ In-Store Qty:",
            qtyInStore,
            "Subtotal:",
            inStoreSubtotal,
            "Co-Op:",
            appliedCoopInStore
          );
          console.log(
            "   ↳ Shipping Qty:",
            qtyShipping,
            "Subtotal:",
            shippingSubtotal,
            "Co-Op:",
            appliedCoopShipping
          );



    console.log("   ↳ Total Coop Applied:", item.coopApplied);
  });

  return cart;
};

export const hasOffsiteProducts = (cart?: Cart) => {
  return Array.isArray(cart?.products)
    ? cart?.products?.some((item) => item.offsite === true)
    : false;
};

export const isEmpty = (cart?: Cart): boolean => {
  return (
    !cart ||
    !Array.isArray(cart?.products) ||
    (cart?.products?.length as number) === 0
  );
};

export const hasShippingAddress = (cart?: Cart): boolean =>
  isNonEmptyString(cart?.shippingData?.shippingAddress?.street) ||
  isNonEmptyString(cart?.shippingData?.shippingAddress?.city) ||
  isNonEmptyString(cart?.shippingData?.shippingAddress?.state) ||
  isNonEmptyString(cart?.shippingData?.shippingAddress?.zip);

export const hasShippingContact = (cart?: Cart): boolean =>
  isNonEmptyString(cart?.shippingData?.shippingContact?.firstName) ||
  isNonEmptyString(cart?.shippingData?.shippingContact?.lastName) ||
  isNonEmptyString(cart?.shippingData?.shippingContact?.email) ||
  isNonEmptyString(cart?.shippingData?.shippingContact?.phone);

export const hasShippingData = (cart?: Cart): boolean =>
  hasShippingAddress(cart) || hasShippingContact(cart);

export const processPreCommitmentAsync = (
  masterDealerId?: string,
  offsite?: boolean,
  mediaId?: string
) =>
  axios.post<ProcessPrecommitmentResponse>(
    combinePaths(baseUrl.ORDER, "processprecommitment") as string,
    {
      masterDealerId,
      offsite,
      mediaId,
    }
  );

// Module-level cache of per-(masterDealerId, mediaId) rates returned from ACB.
// Keys are formatted as `${masterDealerId}:${mediaId}` so cache cannot bleed between
// dealers. Cleared explicitly via clearMediaIdRateCache() when a new dealer is selected
// (so the next recalc fetches fresh rates for that dealer).
const mediaIdRateCache = new Map<string, number>();

const cacheKey = (masterDealerId: string, mediaId: string) => `${masterDealerId}:${mediaId}`;

export const clearMediaIdRateCache = () => {
  mediaIdRateCache.clear();
};

/**
 * For the given dealer and a list of mediaIds, returns a Record<mediaId, rate>.
 * Uses an in-memory cache so repeated calls with the same (dealer, mediaId) only hit
 * the backend once per session. Missing rates default to 0 (fail-closed) — matches
 * the backend's behavior when ACB returns an error or unparseable rate.
 */
export const fetchMediaIdRates = async (
  masterDealerId: string,
  mediaIds: string[]
): Promise<Record<string, number>> => {
  const result: Record<string, number> = {};
  // Deduplicate the mediaIds so we don't call the backend twice for the same value
  // when multiple cart products share a mediaId.
  const uniqueMediaIds = Array.from(new Set(mediaIds.filter(Boolean)));

  const toFetch: string[] = [];
  for (const mediaId of uniqueMediaIds) {
    const cached = mediaIdRateCache.get(cacheKey(masterDealerId, mediaId));
    if (typeof cached === "number") {
      result[mediaId] = cached;
    } else {
      toFetch.push(mediaId);
    }
  }

  if (toFetch.length === 0) return result;

  // Parallel fetch of all uncached mediaIds — typical cart only has 1-3 unique mediaIds,
  // so this is a small fan-out.
  const responses = await Promise.all(
    toFetch.map((mediaId) =>
      processPreCommitmentAsync(masterDealerId, undefined, mediaId)
        .then((response) => ({ mediaId, response }))
        .catch((error) => {
          console.warn(`⚠️ Failed to fetch rate for mediaId ${mediaId}:`, error);
          return { mediaId, response: null };
        })
    )
  );

  for (const { mediaId, response } of responses) {
    const rate =
      response?.status === 200 &&
      response?.data?.errorOccured === false &&
      typeof response?.data?.reimbursementRate === "number"
        ? response.data.reimbursementRate
        : 0;
    mediaIdRateCache.set(cacheKey(masterDealerId, mediaId), rate);
    result[mediaId] = rate;
  }

  return result;
};
